import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PlanName } from '../../../api/types';
import { useAuth } from '../../../app/providers/useAuth';
import { createSubscription } from '../api/billingApi';
import { resolveSubscriptionOutcome } from '../lib/billingState';
import {
  isInvoiceSupported,
  openInvoice,
  openInvoiceFallback,
  type InvoiceStatus,
} from '../lib/telegramInvoice';
import type { ActivationStartPhase } from './useActivationPolling';

// PARA-131 — оформление подписки: POST + исполнение исхода AC-5.
// Один и тот же вызов обслуживает новое оформление, смену тарифа (AC-14) и
// возобновление отменённой подписки (AC-13c) — отдельных эндпоинтов нет.

/** Зачем нажата кнопка: тексты успеха и отказа различаются (AC-13c, AC-E9). */
export type SubscriptionIntent = 'purchase' | 'resume';

export type SubscriptionErrorKind =
  /** 503 / сеть при оформлении или возобновлении (AC-E1, AC-E9). */
  | 'unavailable'
  /** Ответ не соответствует контракту (AC-E8). */
  | 'contractViolation'
  /** Колбэк платёжного шита вернул 'failed' (AC-9). */
  | 'paymentFailed'
  /** Ни платёжного шита, ни фолбэка в этом клиенте (AC-16). */
  | 'invoiceUnsupported';

export interface SubscriptionError {
  kind: SubscriptionErrorKind;
  intent: SubscriptionIntent;
}

interface StartSubscriptionCallbacks {
  /** Подготовка экрана перед КАЖДОЙ отправкой, включая «Попробовать снова»:
   * снимок `expires_at` (AC-10) обязан относиться к текущей попытке. */
  onBeforeStart?: () => void;
  /** AC-6/AC-7/AC-16: экран переходит в ожидание активации. */
  onActivationWait: (plan: PlanName, phase: ActivationStartPhase) => void;
  /** AC-5a/AC-13c: backend уже всё сделал, оплата не требуется. */
  onAlreadyActive: (plan: PlanName, intent: SubscriptionIntent) => void;
}

export interface StartSubscription {
  start: (plan: PlanName, intent?: SubscriptionIntent) => void;
  /** AC-9: «Попробовать снова» повторяет сценарий целиком. */
  retry: () => void;
  isPending: boolean;
  error: SubscriptionError | null;
  clearError: () => void;
}

interface StartVariables {
  plan: PlanName;
  intent: SubscriptionIntent;
}

export function useStartSubscription({
  onBeforeStart,
  onActivationWait,
  onAlreadyActive,
}: StartSubscriptionCallbacks): StartSubscription {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<SubscriptionError | null>(null);
  // AC-E6: защита от двойного нажатия синхронна — состояние мутации к моменту
  // второго клика может ещё не примениться, и ушло бы два POST.
  const inFlightRef = useRef(false);
  const lastRequestRef = useRef<StartVariables | null>(null);

  const handleInvoiceStatus = useCallback(
    ({ plan, intent }: StartVariables, status: InvoiceStatus) => {
      if (status === 'paid' || status === 'pending') {
        // AC-6/AC-7: техническая реакция одна, смысл для пользователя разный.
        onActivationWait(plan, status);
        return;
      }
      if (status === 'failed') {
        setError({ kind: 'paymentFailed', intent });
      }
      // 'cancelled' (AC-8) — молчаливый возврат: ни ошибки, ни ожидания.
    },
    [onActivationWait],
  );

  const mutation = useMutation({
    mutationFn: (variables: StartVariables) => createSubscription(variables.plan),
    onSuccess: (response, variables) => {
      const outcome = resolveSubscriptionOutcome(response);
      if (outcome === 'already-active') {
        // AC-5a/AC-E10: источник истины — backend, локально тариф не меняем.
        void queryClient.invalidateQueries({ queryKey: ['billing', 'me', user?.id] });
        onAlreadyActive(variables.plan, variables.intent);
        return;
      }
      if (outcome === 'contract-violation') {
        setError({ kind: 'contractViolation', intent: variables.intent });
        return;
      }
      // Ссылка из ПОСЛЕДНЕГО ответа; на её новизну не закладываемся (Р-8).
      const checkoutUrl = response.checkout_url as string;
      if (isInvoiceSupported()) {
        openInvoice(checkoutUrl, (status) => handleInvoiceStatus(variables, status));
        return;
      }
      // AC-16: колбэка со статусом при фолбэке не будет — ждём как при 'pending'.
      if (openInvoiceFallback(checkoutUrl)) {
        onActivationWait(variables.plan, 'pending');
        return;
      }
      setError({ kind: 'invoiceUnsupported', intent: variables.intent });
    },
    onError: (_error, variables) => {
      setError({ kind: 'unavailable', intent: variables.intent });
    },
    onSettled: () => {
      inFlightRef.current = false;
    },
  });

  const { mutate } = mutation;

  const start = useCallback(
    (plan: PlanName, intent: SubscriptionIntent = 'purchase') => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      lastRequestRef.current = { plan, intent };
      setError(null);
      onBeforeStart?.();
      mutate({ plan, intent });
    },
    [mutate, onBeforeStart],
  );

  const retry = useCallback(() => {
    const last = lastRequestRef.current;
    if (last !== null) {
      start(last.plan, last.intent);
    }
  }, [start]);

  const clearError = useCallback(() => setError(null), []);

  return { start, retry, isPending: mutation.isPending, error, clearError };
}
